import React from 'react'

import { useIntl } from 'react-intl'
import { useMutation } from '@tanstack/react-query'
import { Send as SendIcon } from '@mui/icons-material'

import { Button } from '../Button'
import DataTable from '../DataTable'
import { useAlert } from '../../hooks'
import { getTheme } from '../../config'
import useUser from '../../hooks/useUser'
import ButtonGroup from '../Button/ButtonGroup'
import { applyMask } from '../../services/utils'
import { formatDate } from '../../services/helper'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import { useLoanContext } from '../../contexts/LoanContext'
import { addSpacesToPascalCase } from '../../services/helpers'
import LoanUsersTablesActionsButton from './LoanUsersTablesTitleActions'

const theme = getTheme()

export type LoanUsersTableProps = {
	title?: string
}

type DataRowProps = {
	id: string
	name: string
	email: string
	phone?: string | null
	role: string
	createdAt: string | null
}

const DEFAULT_SORT_COLUMN = 'createdAt'

const LoanUsersTable = ({ title }: LoanUsersTableProps) => {
	const { dataTable: breakpoint } = theme.breakpoints

	const { formatMessage } = useIntl()
	const { alert } = useAlert()
	const {
		loan,
		isLoading: isLoanLoading,
		isRefetching: isLoanRefetching,
		refetch,
	} = useLoanContext()
	const { isBorrower } = useUser()

	const sendInviteReminderMutation = useMutation({
		mutationFn: (userId: string) =>
			TheBigPOSApi.sendLoanUserInviteReminderNotification(
				loan?.loanID ?? '',
				userId
			),
	})

	const loanUsers: DataRowProps[] =
		loan?.userLoans.map((userLoan) => ({
			id: userLoan.user?.id ?? '',
			name: `${userLoan.user?.firstName} ${userLoan.user?.lastName}`,
			email: userLoan.user?.email ?? '',
			phone: userLoan.user?.phone,
			role: addSpacesToPascalCase(userLoan.role || ''),
			createdAt: formatDate(userLoan.user?.createdAt, false),
		})) || []

	const loanOfficer: DataRowProps[] = loan?.loanOfficer?.email
		? [
				{
					id: loan.loanOfficer.id,
					name: loan.loanOfficer.name ?? '',
					email: loan.loanOfficer.email,
					phone: loan.loanOfficer.phone,
					role: 'LoanOfficer',
					createdAt:
						loan.loanOfficer.siteConfiguration?.createdAt ?? '',
				},
			]
		: []

	const data: DataRowProps[] = [...loanUsers, ...loanOfficer]

	const handleInviteClick = async (row: DataRowProps) => {
		try {
			await sendInviteReminderMutation.mutateAsync(row.id)
			alert(formatMessage({ id: 'alerts.invites_success' }))
		} catch {
			alert(formatMessage({ id: 'alerts.invites_failed' }), {
				severity: 'error',
			})
		}
	}

	const columns = [
		{
			name: formatMessage({ id: 'global.name' }),
			selector: (row: DataRowProps) => row.name,
			cell: (row: DataRowProps) => row.name,
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.email' }),
			selector: (row: DataRowProps) => row.email || '',
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.phone_number' }),
			cell: (row: DataRowProps) => applyMask(row.phone, 'phone'),
		},
		{
			name: formatMessage({ id: 'global.role' }),
			selector: (row: DataRowProps) => {
				if (!row.role) {
					return ''
				}
				return addSpacesToPascalCase(row.role)
			},
		},
		{
			name: formatMessage({ id: 'global.created' }),
			selector: (row: DataRowProps) => row.createdAt ?? '',
			cell: (row: DataRowProps) => formatDate(row.createdAt, false),
			sortable: true,
			sortField: 'createdAt',
			hide: breakpoint,
		},
		{
			name: '',
			omit: isBorrower,
			cell: (row: DataRowProps) => {
				if (row.role === 'LoanOfficer') return null
				return (
					<ButtonGroup
						overflowLimit={1}
						items={[
							<Button
								key="LoanUsersTable__inviteReminder"
								onClick={() => handleInviteClick(row)}
								startIcon={<SendIcon />}
								text={formatMessage({
									id: 'invites.invite_reminder',
								})}
								variant="text"
								className="dark:!text-white"
							/>,
						]}
					/>
				)
			},
		},
	]

	return (
		<div>
			<DataTable
				title={title}
				onRefreshClick={refetch}
				data={data}
				columns={columns}
				defaultSortAsc={false}
				defaultSortFieldId={DEFAULT_SORT_COLUMN}
				progressPending={isLoanLoading && !isLoanRefetching}
				keyField="id"
				titleActions={[
					<LoanUsersTablesActionsButton key="actions-button" />,
				]}
			/>
		</div>
	)
}

export default LoanUsersTable
